import React, { useEffect, useState } from 'react'
import { format } from 'date-fns'
import { FiCalendar, FiClock, FiInstagram, FiMapPin, FiPhone, FiUser } from 'react-icons/fi'
import { salonAPI } from '../services/api'
import LoadingSpinner from '../components/LoadingSpinner'
import { useStudio } from '../context/StudioContext'

export default function Booking() {
  const { studio } = useStudio()
  const [services, setServices] = useState([])
  const [staff, setStaff] = useState([])
  const [loading, setLoading] = useState(true)
  const [loadingTimes, setLoadingTimes] = useState(false)
  const [availableTimes, setAvailableTimes] = useState([])
  const [formData, setFormData] = useState({
    clientName: '',
    clientEmail: '',
    clientPhone: '',
    serviceId: '',
    staffId: '',
    bookingDate: '',
    startTime: '',
  })
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [servicesRes, staffRes] = await Promise.all([
          salonAPI.getServices(),
          salonAPI.getStaff(),
        ])
        setServices(servicesRes.data)
        setStaff(staffRes.data)
      } catch (err) {
        console.error('Error fetching data:', err)
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [])

  useEffect(() => {
    const fetchTimes = async () => {
      if (!formData.serviceId || !formData.bookingDate) {
        setAvailableTimes([])
        setFormData((prev) => ({ ...prev, startTime: '' }))
        return
      }

      setLoadingTimes(true)
      try {
        const response = await salonAPI.getAvailableTimes(formData.serviceId, formData.bookingDate)
        setAvailableTimes(response.data)
      } catch (err) {
        console.error('Error fetching available times:', err)
        setAvailableTimes([])
      } finally {
        setLoadingTimes(false)
      }
    }

    fetchTimes()
  }, [formData.serviceId, formData.bookingDate])

  const handleChange = (event) => {
    const { name, value } = event.target
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  const handleSubmit = async (event) => {
    event.preventDefault()
    setError('')

    if (!formData.clientName || !formData.clientEmail || !formData.serviceId || !formData.bookingDate) {
      setError('Please fill in all required fields')
      return
    }

    if (!formData.startTime) {
      setError('Please choose an available time slot')
      return
    }

    try {
      const startDateTime = new Date(`${formData.bookingDate}T${formData.startTime}:00`)
      const bookingData = {
        ...formData,
        serviceId: parseInt(formData.serviceId, 10),
        staffId: formData.staffId ? parseInt(formData.staffId, 10) : null,
        bookingDate: startDateTime,
        startTime: startDateTime,
        endTime: startDateTime,
      }

      await salonAPI.createBooking(bookingData)
      setSubmitted(true)
      setFormData({
        clientName: '',
        clientEmail: '',
        clientPhone: '',
        serviceId: '',
        staffId: '',
        bookingDate: '',
        startTime: '',
      })
      setAvailableTimes([])
      setTimeout(() => setSubmitted(false), 5000)
    } catch (err) {
      setError(err.response?.data || 'Failed to book appointment. Please try again.')
      console.error('Booking error:', err)
    }
  }

  const selectedService = services.find((service) => service.id === Number(formData.serviceId))

  if (loading) return <LoadingSpinner />

  return (
    <div className="pb-20">
      <section className="section-shell page-section">
        <div className="surface-card px-6 py-10 sm:px-10">
          <p className="eyebrow">Booking</p>
          <h1 className="mt-4 font-heading text-5xl text-stone-900 sm:text-6xl">Reserve time with the studio.</h1>
          <p className="mt-4 max-w-3xl text-sm leading-7 text-stone-600 sm:text-base">
            Choose your service, select a date, and book one of the available time slots. If you would rather confirm details directly, the studio also books through phone and Instagram.
          </p>
        </div>
      </section>

      <section className="section-shell">
        <div className="grid gap-6 xl:grid-cols-[1fr_0.8fr]">
          <div className="surface-card p-6 sm:p-8">
            {submitted && (
              <div className="mb-6 rounded-[1.5rem] border border-emerald-200 bg-emerald-50 p-5 text-emerald-800">
                <p className="text-xs font-semibold uppercase tracking-[0.16em]">Booking received</p>
                <p className="mt-2 text-sm leading-7">Your request was saved successfully. The studio can follow up with confirmation details.</p>
              </div>
            )}

            {error && (
              <div className="mb-6 rounded-[1.5rem] border border-red-200 bg-red-50 p-5 text-red-800">
                <p>{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-8">
              <div>
                <p className="eyebrow">Your details</p>
                <div className="mt-5 space-y-4">
                  <div>
                    <label className="form-label">
                      <FiUser className="mr-2 inline" />Full Name *
                    </label>
                    <input
                      type="text"
                      name="clientName"
                      value={formData.clientName}
                      onChange={handleChange}
                      className="input-field"
                      placeholder="Your full name"
                      required
                    />
                  </div>

                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div>
                      <label className="form-label">Email *</label>
                      <input
                        type="email"
                        name="clientEmail"
                        value={formData.clientEmail}
                        onChange={handleChange}
                        className="input-field"
                        placeholder="your@email.com"
                        required
                      />
                    </div>

                    <div>
                      <label className="form-label">
                        <FiPhone className="mr-2 inline" />Phone
                      </label>
                      <input
                        type="tel"
                        name="clientPhone"
                        value={formData.clientPhone}
                        onChange={handleChange}
                        className="input-field"
                        placeholder="(555) 123-4567"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <p className="eyebrow">Service</p>
                <div>
                  <label className="form-label">Service *</label>
                  <select
                    name="serviceId"
                    value={formData.serviceId}
                    onChange={handleChange}
                    className="input-field"
                    required
                  >
                    <option value="">Choose a service...</option>
                    {services.map((service) => (
                      <option key={service.id} value={service.id}>
                        {service.name} - ${service.price} ({service.durationMinutes} min)
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="form-label">Preferred studio contact</label>
                <select
                  name="staffId"
                  value={formData.staffId}
                  onChange={handleChange}
                  className="input-field"
                >
                  <option value="">Any available staff member</option>
                  {staff.map((member) => (
                    <option key={member.id} value={member.id}>
                      {member.name} - {member.specialty}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <p className="eyebrow">Date and time</p>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <label className="form-label">
                      <FiCalendar className="mr-2 inline" />Date *
                    </label>
                    <input
                      type="date"
                      name="bookingDate"
                      value={formData.bookingDate}
                      onChange={handleChange}
                      min={new Date().toISOString().split('T')[0]}
                      className="input-field"
                      required
                    />
                  </div>

                  <div>
                    <label className="form-label">
                      <FiClock className="mr-2 inline" />Time *
                    </label>
                    <div className="rounded-[1.5rem] border border-stone-200 bg-stone-50 px-4 py-4 text-sm text-stone-600">
                      {formData.startTime ? (
                        <span className="font-semibold text-stone-900">{formData.startTime}</span>
                      ) : (
                        'Select a service and date to unlock available times.'
                      )}
                    </div>
                  </div>
                </div>

                <div className="mt-5 rounded-[1.75rem] bg-stone-100/90 p-5">
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-stone-500">Available time slots</p>
                    {loadingTimes && <span className="text-xs uppercase tracking-[0.18em] text-stone-500">Loading</span>}
                  </div>
                  <div className="mt-4 flex flex-wrap gap-3">
                    {!loadingTimes && availableTimes.length === 0 && formData.serviceId && formData.bookingDate && (
                      <p className="text-sm text-stone-600">No open slots for that day. Try another date or call the studio.</p>
                    )}
                    {availableTimes.map((slot) => {
                      const slotDate = new Date(slot)
                      const slotValue = format(slotDate, 'HH:mm')
                      const slotLabel = format(slotDate, 'h:mm a')
                      const isSelected = formData.startTime === slotValue

                      return (
                        <button
                          key={slot}
                          type="button"
                          onClick={() => setFormData((prev) => ({ ...prev, startTime: slotValue }))}
                          className={isSelected ? 'filter-pill-active' : 'filter-pill'}
                        >
                          {slotLabel}
                        </button>
                      )
                    })}
                  </div>
                </div>
              </div>

              <button type="submit" className="primary-button w-full justify-center">
                Confirm Booking
              </button>
            </form>
          </div>

          <aside className="space-y-6">
            {selectedService && (
              <div className="surface-card p-6">
                <p className="eyebrow">Selected service</p>
                <h2 className="mt-4 font-heading text-4xl text-stone-900">{selectedService.name}</h2>
                <p className="mt-3 text-sm leading-7 text-stone-600">{selectedService.description}</p>
                <div className="mt-5 grid gap-3 sm:grid-cols-2">
                  <div className="rounded-[1.5rem] bg-stone-100 px-4 py-4">
                    <p className="text-xs uppercase tracking-[0.18em] text-stone-500">Duration</p>
                    <p className="mt-2 font-semibold text-stone-900">{selectedService.durationMinutes} minutes</p>
                  </div>
                  <div className="rounded-[1.5rem] bg-rose-50 px-4 py-4">
                    <p className="text-xs uppercase tracking-[0.18em] text-stone-500">Starting at</p>
                    <p className="mt-2 font-semibold text-rose-700">${selectedService.price.toFixed(2)}</p>
                  </div>
                </div>
              </div>
            )}

            <div className="surface-card p-6">
              <p className="eyebrow">Direct contact</p>
              <div className="mt-5 space-y-4">
                <a href={studio ? `tel:+1${studio.phone}` : 'tel:+18138503700'} className="booking-link">
                  <FiPhone className="text-rose-700" />
                  <span>{studio?.phoneDisplay || '(813) 850-3700'}</span>
                </a>
                <a href={studio?.instagramUrl || 'https://www.instagram.com/maya__nails/'} target="_blank" rel="noopener noreferrer" className="booking-link">
                  <FiInstagram className="text-rose-700" />
                  <span>{studio?.handle || '@maya__nails'}</span>
                </a>
                <div className="booking-link">
                  <FiMapPin className="text-rose-700" />
                  <span>{studio?.city || 'Tampa, Florida'}</span>
                </div>
              </div>
            </div>

            <div className="surface-card p-6">
              <p className="eyebrow">Before you arrive</p>
              <div className="mt-5 space-y-4 text-sm leading-7 text-stone-600">
                <p>Choose the service that best matches your goal so the studio can prepare the right timing.</p>
                <p>Use the note field in the future if you want to add nail inspiration, laser area details, or beauty preferences.</p>
                <p>Bilingual support is available for guests who prefer English or Espanol.</p>
              </div>
            </div>
          </aside>
        </div>
      </section>
    </div>
  )
}
